import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

// Streaming helper (inline — like useChatStream but pointed at agent endpoint)
async function streamAgentChat({ slug, conv_id, message, guest, onMeta, onDelta, onDone, onError, signal }) {
  const resp = await fetch(`${API}/agents/${slug}/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ conv_id, message, guest }),
    signal,
  });
  if (!resp.ok) {
    onError?.(`HTTP ${resp.status}`);
    return;
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, idx).trim();
      buffer = buffer.slice(idx + 2);
      if (!frame.startsWith("data:")) continue;
      try {
        const ev = JSON.parse(frame.slice(5).trim());
        if (ev.type === "meta") onMeta?.(ev);
        else if (ev.type === "delta") onDelta?.(ev.content);
        else if (ev.type === "done") onDone?.();
        else if (ev.type === "error") onError?.(ev.message);
      } catch { /* skip */ }
    }
  }
  onDone?.();
}

export default function AgentDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [agent, setAgent] = useState(null);
  const [convs, setConvs] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);

  const loadConvs = useCallback(async () => {
    if (!user || !slug) return;
    try {
      const { data } = await api.get(`/agents/${slug}/conversations`);
      setConvs(data.items || []);
    } catch { /* silent */ }
  }, [user, slug]);

  useEffect(() => {
    if (!slug) return;
    api.get(`/agents/${slug}`).then(({ data }) => setAgent(data)).catch(() => {
      toast.error("Agent not found");
    });
    loadConvs();
    setActiveConv(null);
    setMessages([]);
  }, [slug, loadConvs]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const openConv = async (id) => {
    setActiveConv(id);
    setMessages([]);
    try {
      const { data } = await api.get(`/agents/${slug}/conversations/${id}`);
      setMessages(data.messages || []);
    } catch {
      toast.error("Failed to load conversation");
    }
  };

  const newConv = () => {
    setActiveConv(null);
    setMessages([]);
    setInput("");
  };

  const send = async (text = input) => {
    if (!text.trim() || streaming) return;
    const guest = !user;
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "", pending: true }]);
    setInput("");
    setStreaming(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    let newConvId = activeConv;
    try {
      await streamAgentChat({
        slug,
        conv_id: activeConv,
        message: text,
        guest,
        signal: ctrl.signal,
        onMeta: (ev) => {
          if (ev.conv_id && ev.conv_id !== activeConv) {
            newConvId = ev.conv_id;
            setActiveConv(ev.conv_id);
          }
        },
        onDelta: (chunk) => {
          setMessages((m) => {
            const arr = m.slice();
            const last = arr[arr.length - 1];
            if (last && last.role === "assistant") arr[arr.length - 1] = { ...last, content: (last.content || "") + chunk };
            return arr;
          });
        },
        onDone: () => {
          setMessages((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, pending: false } : x)));
        },
        onError: (err) => {
          toast.error(err || "Stream error");
          setMessages((m) => m.filter((x) => !x.pending));
        },
      });
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message);
    } finally {
      setStreaming(false);
      abortRef.current = null;
      if (!activeConv && newConvId) setActiveConv(newConvId);
      if (user) loadConvs();
    }
  };

  if (!agent) {
    return (
      <div className="p-8 text-center text-xs font-mono-data text-[color:var(--text-muted)]">Loading agent…</div>
    );
  }

  const starterPrompts = starterFor(agent.id);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] h-[calc(100vh-6rem)]" data-testid={`agent-page-${slug}`}>
      {/* Sidebar */}
      <aside className="border-r border-[color:var(--border-default)] bg-[color:var(--bg-surface)] flex flex-col">
        <div className="p-3 border-b border-[color:var(--border-default)]">
          <Link
            to="/agents"
            className="text-[10px] font-mono-data text-[color:var(--text-muted)] hover:text-white tracking-widest"
            data-testid="back-to-agents"
          >
            ← ALL AGENTS
          </Link>
          <button
            data-testid="new-agent-conv-btn"
            onClick={newConv}
            className="w-full mt-2 bg-white text-black py-2 text-[11px] font-mono-data tracking-widest hover:opacity-90"
          >
            + NEW CHAT
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {!user ? (
            <div className="p-4 text-[11px] font-mono-data text-[color:var(--text-muted)] leading-relaxed">
              GUEST MODE — sign in to save chats with this specialist.
            </div>
          ) : convs.length === 0 ? (
            <div className="p-4 text-[11px] font-mono-data text-[color:var(--text-muted)]">
              No chats yet with {agent.name}.
            </div>
          ) : (
            convs.map((c) => (
              <button
                key={c.id}
                data-testid={`agent-conv-${c.id}`}
                onClick={() => openConv(c.id)}
                className={`w-full text-left px-3 py-2 border-b border-[color:var(--border-default)] cursor-pointer ${
                  activeConv === c.id ? "bg-[color:var(--bg-hover)]" : "hover:bg-[color:var(--bg-elevated)]"
                }`}
              >
                <div className="text-xs text-white truncate">{c.title}</div>
                <div className="text-[9px] font-mono-data text-[color:var(--text-muted)]">
                  {(c.updated_at || "").slice(5, 16).replace("T", " ")}
                </div>
              </button>
            ))
          )}
        </div>
      </aside>

      {/* Main */}
      <section className="flex flex-col bg-[color:var(--bg-surface)]">
        <div className="border-b border-[color:var(--border-default)] px-4 py-3 flex items-center gap-3">
          <div className="w-3 h-3 shrink-0" style={{ backgroundColor: agent.color }} aria-hidden />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono-data tracking-widest text-gold">
              ◆ {agent.category} · {agent.tier?.toUpperCase()}
            </div>
            <div className="font-serif-elite text-xl mt-0.5 text-white truncate">{agent.name}</div>
            <div className="text-xs text-[color:var(--text-secondary)] font-body truncate">{agent.role}</div>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-6" data-testid="agent-messages">
          {messages.length === 0 ? (
            <div className="max-w-2xl mx-auto py-12">
              <div className="font-serif-elite text-3xl text-white text-center">
                Talk to {agent.name}
              </div>
              <div className="text-sm text-[color:var(--text-secondary)] mt-2 text-center">
                Ask anything within their specialty. Live market snapshot is auto-injected.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-6">
                {starterPrompts.map((p) => (
                  <button
                    key={p}
                    data-testid={`starter-${p.slice(0, 10)}`}
                    onClick={() => send(p)}
                    className="border border-[color:var(--border-default)] p-3 text-xs text-white text-left hover:bg-[color:var(--bg-elevated)] transition-colors"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-4">
              {messages.map((m, i) => (
                <div key={`${activeConv || "new"}-${i}`} data-testid={`msg-${m.role}-${i}`} className={m.role === "user" ? "ml-12" : "mr-12"}>
                  <div
                    className={`text-[10px] font-mono-data tracking-widest mb-1 ${
                      m.role === "user" ? "text-[color:var(--text-muted)] text-right" : ""
                    }`}
                    style={m.role !== "user" ? { color: agent.color } : {}}
                  >
                    {m.role === "user" ? "YOU" : `◆ ${agent.name.toUpperCase()}`}
                  </div>
                  <div
                    className={`whitespace-pre-wrap font-body text-sm leading-relaxed ${
                      m.role === "user"
                        ? "text-white bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] p-3"
                        : "text-white"
                    }`}
                  >
                    {m.content}
                    {m.pending && <span className="inline-block w-1.5 h-4 bg-white ml-1 animate-pulse align-middle" />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-[color:var(--border-default)] p-3">
          <div className="max-w-3xl mx-auto flex gap-2">
            <textarea
              data-testid="agent-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={`Ask ${agent.name}…`}
              rows={2}
              className="flex-1 bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] px-3 py-2 text-sm text-white placeholder:text-[color:var(--text-muted)] resize-none focus:outline-none focus:border-[color:var(--border-focus)]"
            />
            {streaming ? (
              <button data-testid="stop-agent-btn" onClick={() => abortRef.current?.abort()} className="bg-[#FF3B30] text-white px-4 text-[11px] font-mono-data tracking-widest hover:opacity-90">
                STOP
              </button>
            ) : (
              <button data-testid="send-agent-btn" onClick={() => send()} disabled={!input.trim()} className="bg-white text-black px-4 text-[11px] font-mono-data tracking-widest hover:opacity-90 disabled:opacity-40">
                SEND →
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function starterFor(id) {
  const map = {
    "macro-economist": ["What growth-inflation quadrant are we in right now?", "How should I position if we get a soft landing?"],
    "gold": ["Is gold's bull run over?", "Gold vs silver — which is better next 6 months?"],
    "crypto": ["What's BTC's on-chain telling us this week?", "ETH/BTC — accumulate or rotate out?"],
    "indian-market": ["Nifty at record highs — is this froth or fair?", "Best BankNifty option strategy this expiry?"],
    "us-market": ["Is the Mag 7 concentration risk real?", "Which sector rotation trade is best now?"],
    "options": ["Design an iron condor on SPY for this month", "Best strategy for high IV environment?"],
    "smart-money": ["Any unusual options activity this week worth acting on?", "Which stocks are hedge funds accumulating?"],
    "risk-officer": ["What's my portfolio's tail risk right now?", "How much cash should I hold?"],
    "black-swan": ["What are the biggest hidden fragilities in markets today?", "Best convex hedges under $1k/month?"],
  };
  return map[id] || [
    `What's your top view right now?`,
    `Walk me through your framework.`,
  ];
}
